(function ($) {
    "use strict";

    /**
     * LogoRow Class
     *
     * This class manages the logo row functionality, including loading items,
     * handling animation, and managing the row state.
     *
     * @class LogoRow
     * @property {string} eventPrefix - Prefix for custom events.
     * @property {HTMLElement} element - The DOM element for the logo row.
     * @property {jQuery} $el - jQuery object for the logo row element.
     * @property {string} id - Unique identifier for the logo row instance.
     * @property {jQuery} itemsContainer - jQuery object for the items container.
     * @property {HTMLElement} animationContainer - The animation container element.
     * @property {number} originalCount - Original number of items in the row.
     * @property {object} options - Options for the logo row instance.
     * @property {number} originalAnimationContainerWidth - Original width of the animation container.
     * @property {HTMLElement} track - The track element for animation.
     * @property {HTMLElement} initialGroup - The initial group element.
     * @property {HTMLElement} cloneGroup - The clone group element.
     * @property {number} gap - Gap between items.
     *
     * @event LogoRow:created - Triggered when a new LogoRow instance is created.
     * @event LogoRow:beforeRender - Triggered before rendering the logo row animation elements.
     * @event LogoRow:afterRender - Triggered after rendering the logo row animation elements.
     * @event LogoRow:play - Triggered when the logo row animation starts playing.
     */
    class LogoRow {
        eventPrefix = "LogoRow:";

        constructor(element, id) {
            this._defaults = {
                animationType: 'carousel',
                animationSpeed: 50,
            };

            this.element = element;
            this.$el = jQuery(element);
            this.id = this.$el.attr("id") || "logo-row-" + id;
            this.itemsContainer = this.$el.find(".items");
            this.animationContainer = null;

            this.originalCount = this.itemsContainer.find('> *').length;
            this.options = { ...this._defaults, ...this.$el.data() };
            
            this.originalAnimationContainerWidth = null;
            this.track = null;
            this.initialGroup = null;
            this.cloneGroup = null;
            this.gap = 0;

            this.init();
            this.render();
            this.play();
        }

        get count() {
            return this.itemsContainer.find('> *').length;
        }

        init() {
            this.emit("created", this);
        }

        render() {
            this.emit("beforeRender", this);

            const items = this.itemsContainer.find('> *');

            const container = document.createElement('div');
            container.classList.add('animation-container');
            container.classList.add('animation--carousel'); //eventually make this dynamic based on selected animation

            const track = document.createElement('div');
            track.classList.add('track');
            container.appendChild(track);

            const groupInitial = document.createElement('div');
            groupInitial.classList.add('group');
            groupInitial.classList.add('group--initial');
            track.appendChild(groupInitial);

            const groupClone = document.createElement('div');
            groupClone.classList.add('group');
            groupClone.classList.add('group--clone');
            track.appendChild(groupClone);

            items.each(function () {
                const clone = this.cloneNode(true);

                clone.querySelector('img').setAttribute('src', clone.querySelector('img').getAttribute('data-src'));

                groupInitial.appendChild(this);
                groupClone.appendChild(clone);
            });

            this.itemsContainer.empty().append(container);

            this.track = this.$el.find('.track')[0];
            this.animationContainer = this.$el.find('.animation-container')[0];
            this.originalAnimationContainerWidth = this.animationContainer.offsetWidth;
            this.groupClone = this.$el.find('.group--clone')[0];
            this.groupInitial = this.$el.find('.group--initial')[0];
            this.gap = this.getGap();

            this.element.classList.add('logo-row--rendered');

            this.emit("afterRender", this);
        }

        play() {
            this.emit("play", this);

            const track = this.track;
            const speed = this.options.animationSpeed;
            const gap = this.gap;

            let start = null;

            const step = (timestamp) => {
                if (!start) start = timestamp;
                const elapsed = timestamp - start;
                const distance = (speed * elapsed) / 1000;
                const loopWidth = (track.scrollWidth + gap) / 2;
                const translateDistance = distance % loopWidth;

                track.style.transform = `translate3d(-${translateDistance}px, 0, 0)`;

                requestAnimationFrame(step);
            };

            requestAnimationFrame(step);
        }

        emit(eventName, data) {
            this.$el.trigger(`${this.eventPrefix}${eventName}`, [data]);
        }

        
        getGap() {
            const gapElement = this.$el.find('.group--initial');

            if (!gapElement) {
                return 0;
            }

            const computedStyle = gapElement.css('column-gap') ? gapElement.css('column-gap').replace('px', '') : null;
            return parseInt(computedStyle) || 0;
        }
    }

    class LogoRowManager {
        static collection = [];
        static count = 0;

        static create(element) {
            this.count++;
            const instance = new LogoRow(element, this.count);
            this.collection.push(instance);
            return instance;
        }

        static getAll() {
            return this.collection;
        }

        static getById(id) {
            return this.collection.find((instance) => instance.id === id);
        }

        static getByIndex(index) {
            return this.collection[index] || null;
        }
    }

    $(document).ready(function () {
        if ($('body').hasClass('compose-mode')) return; // Avoid initializing in the frontend editor

        $(".syn-logo-row").each(function () {
            LogoRowManager.create(this);
        });
    });
})(jQuery);